import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Alert, Modal } from "react-bootstrap";
import { FaPlus, FaSync, FaTrash } from "react-icons/fa";
import { useAuth } from "../../../AuthContext/AuthContext";
import { useTranslation } from 'react-i18next';
import { useNotification } from "../../../SharedComponents/Notifications/NotificationContext";
import {
    fetchAssetOptions,
    fetchEquipmentData,
    fetchInStockEquipment,
    deleteEquipmentData,
    assignEquipment,
    validateEquipmentFormData,
    getEquipmentById
} from "./EquipmentService";
import { extractDropdownOptions } from './EquipmentUtils';
import EquipmentAssignForm from './EquipmentAssignForm';
import EquipmentList from './EquipmentList';

const EquipmentForm = ({ onValidationChange }) => {
    const { t } = useTranslation('equipment');
    const { currentUser } = useAuth();
    const { showNotification } = useNotification();

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [view, setView] = useState('list');
    const [editIndex, setEditIndex] = useState(null);

    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [deleteItemDetails, setDeleteItemDetails] = useState({
        category: '',
        make: '',
        model: '',
        serial_number: ''
    });

    // Data state
    const [equipments, setEquipments] = useState([]);
    const [inStockEquipments, setInStockEquipments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [makes, setMakes] = useState([]);

    // Form data for assignment
    const [assignmentData, setAssignmentData] = useState({
        equipment_id: "",
        delivery_type: "",
        category: "",
        make: "",
        model: "",
        serial_number: "",
        device_condition: "",
        date_assigned: "",
        delivered_by: "",
        status_of_equipment: "Assigned" // Default for assignment
    });

    // Debug: Log assignment data changes
    useEffect(() => {
        console.log("Assignment data changed:", assignmentData);
    }, [assignmentData]);

    // Check if status is available (In Stock or Not Used)
    const isAvailableStatus = (status) => {
        return status === "In Stock" || status === "Not Used" || status === "Available" || status === "Ready";
    };

    // UPDATED: Enhanced load data function with better error handling
    const loadData = useCallback(async () => {
        if (!currentUser) {
            console.log("No user available, cannot fetch data");
            setLoading(false);
            return;
        }

        try {
            console.log("FETCH DATA START:", new Date().toISOString());
            setLoading(true);
            setError(null);

            // Fetch asset data first
            console.log("Fetching asset options...");
            const assetData = await fetchAssetOptions();

            // Log asset availability
            console.log("Asset data available:", {
                count: assetData?.length || 0,
                hasData: assetData?.length > 0
            });

            // Process asset data for dropdowns
            if (assetData?.length > 0) {
                const options = extractDropdownOptions(assetData);
                setCategories(options.categories || []);
                setMakes(options.makes || []);

                console.log("Asset dropdown options:", {
                    categoriesCount: options.categories?.length || 0,
                    makesCount: options.makes?.length || 0
                });
            } else {
                console.log("No asset data available for dropdowns");
                setCategories([]);
                setMakes([]);
            }

            // Continue with equipment data fetch
            console.log("Fetching equipment data...");
            const equipmentData = await fetchEquipmentData(currentUser);

            // Ensure we have valid arrays
            console.log("Setting equipment data:", equipmentData);
            setEquipments(Array.isArray(equipmentData) ? equipmentData : []);

            // UPDATED: Try to fetch in-stock equipment with fallback handling
            console.log("Fetching in-stock equipment...");
            try {
                const inStockData = await fetchInStockEquipment();

                // Filter to ensure only available items are in the in-stock list
                const availableEquipment = Array.isArray(inStockData)
                    ? inStockData.filter(item => isAvailableStatus(item.status_of_equipment || item.status))
                    : [];

                console.log(`Found ${availableEquipment.length} available items out of ${inStockData?.length || 0}`);
                setInStockEquipments(availableEquipment);
            } catch (inStockError) {
                console.warn("Could not fetch in-stock equipment:", inStockError.message);
                // Continue without in-stock data - this prevents the infinite loading loop
                setInStockEquipments([]);

                // Only show error notification if it's not a permission issue
                if (!inStockError.message.includes('403') && !inStockError.message.includes('Forbidden')) {
                    showNotification("warning", t('couldNotLoadAvailableEquipment') || "Could not load available equipment for assignment");
                }
            }

            console.log("All data fetched successfully");

        } catch (err) {
            console.error("Error loading data:", err);
            setError(t('failedToLoadData') + ": " + (err.message || t('errorMessage')));
            showNotification("error", t('failedToLoadData') || "Failed to load data");
        } finally {
            console.log("FETCH DATA END:", new Date().toISOString());
            setLoading(false);
        }
    }, [currentUser, showNotification, t]);

    // Load data on component mount
    useEffect(() => {
        console.log("Component mounted, loading data");
        loadData();
    }, [loadData]);

    // Refresh handler
    const handleRefresh = () => {
        loadData();
    };

    // Show the assignment form
    const showAssignForm = () => {
        setAssignmentData({
            equipment_id: "",
            delivery_type: "",
            category: "",
            make: "",
            model: "",
            serial_number: "",
            device_condition: "",
            date_assigned: "",
            delivered_by: "",
            status_of_equipment: "Assigned" // Default for assignment
        });

        setView('assign');
        setError(null);
    };

    // Show equipment list
    const cancelForm = () => {
        setView('list');
        setError(null);
    };

    // Cancel loading state
    const handleCancel = () => {
        setLoading(false);
    };

    // FIXED: Assignment handler with improved error handling and data refresh
    const handleAssignmentSave = async () => {
        try {
            console.log("ASSIGNMENT SAVE CLICKED - Data:", assignmentData);
            setLoading(true);
            setError(null);

            // Validate form data
            const validationErrors = validateEquipmentFormData(assignmentData);

            if (Object.keys(validationErrors).length > 0) {
                setError(validationErrors);
                setLoading(false);
                return;
            }

            // Verify equipment_id is set
            if (!assignmentData.equipment_id) {
                setError({
                    equipment_id: t('selectValidEquipment') || "Please select a valid category, make, and model to assign equipment"
                });
                setLoading(false);
                return;
            }

            // Verify serial_number is set
            if (!assignmentData.serial_number) {
                setError({
                    serial_number: t('selectSerialNumber') || "Please select a serial number"
                });
                setLoading(false);
                return;
            }

            console.log("Sending assignment data to API:", {
                equipmentId: assignmentData.equipment_id,
                data: assignmentData,
                user: currentUser
            });

            // Use the assignment function
            const result = await assignEquipment(
                assignmentData.equipment_id,
                assignmentData,
                currentUser
            );

            console.log("Assignment API response:", result);
            showNotification("success", t('equipmentAssigned') || "Equipment assigned successfully");

            // Remove assigned equipment from in-stock list
            const updatedInStock = inStockEquipments.filter(
                item => item.id.toString() !== assignmentData.equipment_id.toString()
            );
            setInStockEquipments(updatedInStock);

            // FIXED: Refresh equipment data from the server instead of just appending
            console.log("Refreshing equipment data from server...");
            const refreshedEquipments = await fetchEquipmentData(currentUser);
            setEquipments(Array.isArray(refreshedEquipments) ? refreshedEquipments : []);

            // Reset form and go back to list
            setView('list');

            if (onValidationChange) {
                onValidationChange(refreshedEquipments);
            }
        } catch (err) {
            console.error("Error assigning equipment:", err);

            // Provide more detailed error information
            let errorMessage = t('failedToAssignEquipment') || "Failed to assign equipment.";
            if (err.response) {
                console.error("Error response status:", err.response.status);
                console.error("Error response data:", err.response.data);

                if (err.response.status === 404) {
                    errorMessage = t('equipmentNotFound') || "Equipment not found or not available for assignment.";
                } else if (err.response.status === 400) {
                    errorMessage = t('equipmentNotAvailable') || "Equipment is not available for assignment.";
                } else if (err.response.data && err.response.data.detail) {
                    errorMessage += " " + err.response.data.detail;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError({
                serial_number: errorMessage
            });
            showNotification("error", errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Open the delete confirmation modal
    const confirmDelete = (index) => {
        const equipmentToDelete = equipments[index];
        setDeleteIndex(index);
        setDeleteItemDetails({
            category: equipmentToDelete.category,
            make: equipmentToDelete.make,
            model: equipmentToDelete.model,
            serial_number: equipmentToDelete.serial_number
        });
        setShowDeleteModal(true);
    };

    // FIXED: Delete handler with improved data refresh
    const handleDelete = async () => {
        try {
            setLoading(true);
            setShowDeleteModal(false);

            const index = deleteIndex;
            const recordToDelete = equipments[index];

            console.log("Deleting equipment:", recordToDelete);
            const deleteResult = await deleteEquipmentData(recordToDelete.id);

            console.log("Delete result:", deleteResult);

            // FIXED: Refresh equipment data from server instead of filtering local state
            console.log("Refreshing equipment data after deletion...");
            const refreshedEquipments = await fetchEquipmentData(currentUser);
            setEquipments(Array.isArray(refreshedEquipments) ? refreshedEquipments : []);

            // Also refresh in-stock equipment in case the deleted item should now be available
            try {
                const refreshedInStock = await fetchInStockEquipment();
                const availableEquipment = Array.isArray(refreshedInStock)
                    ? refreshedInStock.filter(item => isAvailableStatus(item.status_of_equipment || item.status))
                    : [];
                setInStockEquipments(availableEquipment);
            } catch (inStockError) {
                console.warn("Could not refresh in-stock equipment:", inStockError.message);
            }

            // Show success message with details from server response
            let successMessage = t('deleteEquipmentMessage') || "Equipment deleted successfully";
            if (deleteResult && deleteResult.asset_updated) {
                successMessage += " " + (t('assetUpdatedMessage') || "and associated asset made available");
            } else if (deleteResult && deleteResult.asset_update_details) {
                successMessage += ` (${deleteResult.asset_update_details})`;
            }

            showNotification("success", successMessage);

            if (onValidationChange) {
                onValidationChange(refreshedEquipments);
            }
        } catch (err) {
            console.error("Error deleting equipment:", err);
            let errorMessage = t('failedToDeleteEquipment') || "Failed to delete equipment.";

            if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail;
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            showNotification("error", errorMessage);
        } finally {
            setLoading(false);
            setDeleteIndex(null);
            setDeleteItemDetails({
                category: '',
                make: '',
                model: '',
                serial_number: ''
            });
        }
    };

    // Edit handler
    const showEditForm = (index) => {
        // Since we now only support assignment, show notification that editing is not supported
        showNotification("info", t('editingNotSupported') || "Editing equipment is not supported in this version");
    };

    // Check if assets are available
    const hasAssetsAvailable = categories.length > 0 && makes.length > 0;

    return (
        <Container fluid className="equipment-management">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>{t('equipmentManagement') || "Equipment Management"}</h3>
                <div>
                    <Button
                        variant="secondary"
                        className="me-2"
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        <FaSync className={`me-1 ${loading ? 'fa-spin' : ''}`} /> {t('refresh') || "Refresh"}
                    </Button>
                    <Button
                        variant="success"
                        onClick={showAssignForm}
                        disabled={loading || !hasAssetsAvailable}
                    >
                        <FaPlus className="me-1" /> {t('assignEquipment') || "Assign Equipment"}
                    </Button>
                </div>
            </div>

            {error && typeof error === 'string' && (
                <Alert variant="danger">
                    {error}
                </Alert>
            )}

            {loading && (
                <Alert variant="info" className="d-flex justify-content-between align-items-center">
                    <span>{t('loading') || "Loading..."}</span>
                    <Button
                        variant="link"
                        onClick={handleCancel}
                        className="p-0"
                    >
                        ({t('cancel') || "Cancel"})
                    </Button>
                </Alert>
            )}

            {/* Show asset availability warning if no assets available */}
            {!hasAssetsAvailable && !loading && (
                <Alert variant="warning">
                    {t('noAssetsAvailable') || "No assets available for assignment. Please add assets to the system before assigning equipment."}
                </Alert>
            )}

            {/* Render different views based on state */}
            {view === 'list' && !loading && (
                <EquipmentList
                    equipments={equipments}
                    onEdit={showEditForm}
                    onDelete={confirmDelete}
                    loading={loading}
                    t={t}
                />
            )}

            {view === 'assign' && (
                <EquipmentAssignForm
                    assignmentData={assignmentData}
                    setAssignmentData={setAssignmentData}
                    categories={categories}
                    makes={makes}
                    handleAssignmentSave={handleAssignmentSave}
                    handleCancel={cancelForm}
                    error={error}
                    hasAssetsAvailable={hasAssetsAvailable}
                    isAvailableStatus={isAvailableStatus}
                    t={t}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaTrash className="text-danger me-2" />
                        {t('confirmDeletion') || "Confirm Deletion"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('deleteConfirmationMessage') || "Are you sure you want to delete this equipment assignment?"}</p>
                    <div className="bg-light p-3 rounded mb-3">
                        <div><strong>{t('category') || "Category"}:</strong> {deleteItemDetails.category}</div>
                        <div><strong>{t('make') || "Make"}:</strong> {deleteItemDetails.make}</div>
                        <div><strong>{t('model') || "Model"}:</strong> {deleteItemDetails.model}</div>
                        <div><strong>{t('serialNumber') || "Serial Number"}:</strong> {deleteItemDetails.serial_number}</div>
                    </div>
                    <div className="alert alert-info">
                        <small>
                            <strong>{t('note') || "Note"}:</strong> {t('deleteNote') || "This will remove the equipment assignment and make the associated asset available for reassignment."}
                        </small>
                    </div>
                    <p className="text-danger mb-0">{t('actionCannotBeUndone') || "This action cannot be undone."}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        {t('cancel') || "Cancel"}
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDelete}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                {t('deleting') || "Deleting..."}
                            </>
                        ) : (
                            <>
                                <FaTrash className="me-2" />
                                {t('delete') || "Delete"}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default EquipmentForm;